<template>
  <multiselect
      class="base-multiselect"
      :class="{'base-multiselect--hide-options': hideOptions}"
      v-bind="$attrs"
      v-on="$listeners"
      :placeholder="internalPlaceholder"
      :loading="loading"
      :value="value"
      :disabled="disabled"
      @input="$emit('input', $event)"
  >
      <span slot="tag" class="base-multiselect__tag" slot-scope="props">
        <slot name="tag" :option="props.option">{{ $attrs.customLabel ? $attrs.customLabel(props.option) : props.option
          }}</slot>
        <span class="base-multiselect__tag-clear" tabindex="1" @keypress.enter.prevent="props.remove(props.option)"
              @mousedown.prevent="props.remove(props.option)">
          <font-awesome-icon :icon="['far', 'times']" />
        </span>
      </span>
    <span slot="caret" class="base-multiselect__caret" slot-scope="props">
        <font-awesome-icon :icon="['far', 'chevron-up']" @mousedown.prevent.stop="props.toggle" />
      </span>
    <span slot="loading" class="base-multiselect__loading">
        <font-awesome-icon v-show="loading" :icon="['far', 'spinner']" spin />
      </span>
    <template slot="noOptions">
      <slot name="noOptions">List is empty.</slot>
    </template>
  </multiselect>
</template>
<script>
import Multiselect from "vue-multiselect";

export default {
  inheritAttrs: false,
  props: {
    value: {
      type: null,
      default() {
        return [];
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: "Select option"
    },
    hideOptions: {
      type: Boolean,
      default: false
    }
  },
  components: { Multiselect },
  computed: {
    internalPlaceholder() {
      return this.loading && this.disabled ? "Loading..." : this.placeholder;
    }
  }
};
</script>
<style lang="scss">
.base-multiselect {
  @apply tw-relative;

  &--hide-options {
    .multiselect__content-wrapper {
      @apply tw-hidden #{!important};
    }
  }

  .multiselect__content-wrapper {
    @apply tw-absolute
    tw-overflow-hidden
    tw-w-full
    tw-mt-1
    tw-rounded
    tw-shadow
    tw-border
    tw-border-solid
    tw-bg-infinity-input-background
    tw-border-infinity-input-border
    tw-z-50;

    -webkit-overflow-scrolling: touch;
  }

  .multiselect__content {
    @apply
    tw-max-h-60
    tw-overflow-auto
    tw-text-infinity-input-text
    tw-w-full
    tw-m-0
    tw-p-0;
  }

  .multiselect__element {
    @apply tw-list-none
    tw-block
    tw-w-full;
  }

  .multiselect__tags {
    @apply tw-rounded
    tw-border
    tw-border-solid
    tw-border-infinity-input-border
    tw-bg-infinity-input-background
    tw-overflow-hidden
    tw-px-1.5
    tw-py-1.5
    tw-pb-0
    tw-cursor-pointer;
  }

  &.multiselect--disabled {
    .multiselect__tags,
    .base-multiselect__caret {
      @apply tw-cursor-default;
    }
  }

  .multiselect__tags-wrap {
    @apply tw-inline-block;
  }

  .multiselect__strong {
    @apply tw-text-infinity-input-text tw-font-normal tw-pl-1;
  }

  &.multiselect--active &__caret {
    transform: rotateZ(180deg);
  }

  &.multiselect--active .multiselect__strong {
    @apply tw-hidden;
  }

  &__loading {
    @apply tw-absolute
    tw-right-px
    tw-top-px
    tw-text-infinity-input-text
    tw-h-9
    tw-flex
    tw-items-center
    tw-mr-10;
  }

  &__caret {
    @apply tw-block
    tw-text-infinity-input-text
    tw-absolute
    tw-w-10
    tw-h-9
    tw-flex
    tw-items-center
    tw-justify-center
    tw-right-px
    tw-top-px
    tw-cursor-pointer;
  }

  &__tag {
    @apply tw-py-0.5
    tw-inline-flex
    tw-items-center
    tw-m-0
    tw-mr-1.5
    tw-mb-1.5
    tw-pl-2
    tw-pr-1.5
    tw-bg-infinity-button-background
    tw-text-infinity-button-text
    tw-rounded;

    &-clear {
      @apply tw-p-0.5
      tw-px-1
      tw-ml-1
      tw-text-xxs
      tw-inline-block
      tw-rounded
      hover:tw-bg-infinity-button-active-background
      tw-bg-infinity-button-hover-background;
    }
  }

  .multiselect__input {
    @apply tw-border-none
    tw-shadow-none
    tw-p-0
    tw-py-0.5
    tw-pl-1
    tw-mb-1.5
    tw-bg-infinity-input-background
    tw-placeholder-infinity-input-placeholder;
  }

  .multiselect__placeholder, .multiselect__single {
    @apply
    tw-inline-block
    tw-pl-1
    tw-py-0.5
    tw-mb-1.5;
  }

  .multiselect__strong {
    @apply tw-inline-block tw-py-0.5 tw-mb-1.5;
  }

  .multiselect__single {
    @apply tw-text-infinity-input-text;
  }

  .multiselect__placeholder {
    @apply tw-text-infinity-input-placeholder;
  }

  .multiselect__option {
    @apply tw-block
    tw-cursor-pointer
    tw-text-infinity-input-text
    hover:tw-bg-infinity-input-background-emphasized
    tw-px-4
    tw-py-1
    tw-w-full;

    &--selected {
      @apply tw-bg-infinity-input-background-emphasized;
    }
  }
}

/**/
</style>
